import { Injectable } from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {HttpClient} from '@angular/common/http';
import {AuthenticatedUserService} from './authenticated-user.service';
import {ActivatedRoute} from '@angular/router';
import {environment} from '@env/environment';

@Injectable()
export class UserHelpService {

    public helpItems: any[] = [];

    readonly basePath = './docs/';

    readonly APPLICATION_ADMIN_HELP_DOC_ROUTES = {
        'admin/institutions': [
            {question: 'How to request data access?', answerUrl: 'home_vendor/1_data_access.html'},
            {question: 'How to review and resolve consents?', answerUrl: 'home_vendor/2_consents.html'},
            {question: 'How to check district data sync?', answerUrl: 'home_vendor/3_district_data_sync.html'},
            {question: 'Inspect Data Quality', answerUrl: 'home_vendor/4_data_quality.html'},
        ],
        'admin/applications/myapplication/edit': [
            {question: 'How to request data access?', answerUrl: 'application_settings/2_data_requirements.html'},
            {question: 'How to add basic information about your app?', answerUrl: 'application_settings/1_basic_info.html'},
        ],
        'administrations': [
            {question: 'How to create, edit or remove accounts?', answerUrl: 'admin/1_vendor_account.html'},
            {question: 'How to solve authentication issues?', answerUrl: 'admin/3_auth_vendor.html'}
        ],
        'databrowsing': [
            {question: 'How to get access and find data in the system?', answerUrl: 'browsing/1_find.html'},
            {question: 'How to track data changes?', answerUrl: 'browsing/2_history.html'},
            // {question: 'Figure out why some data are missing', answerUrl: 'browsing/3_missing_data.html'}
        ],
        'user/profile': [
            {question: 'How to edit your profile?', answerUrl: 'user_profile/1_vendor_profile.html'},
            {question: 'How to setup email notifications?', answerUrl: 'user_profile/2_vendor_notification.html'},
        ]
    };

    readonly INSTITUTIONAL_ADMIN_HELP_DOC_ROUTES = {
        'administrations': [
            {question: 'How to create, edit or remove accounts?', answerUrl: 'admin/1_provider_account.html'},
            {question: 'How to solve authentication issues?', answerUrl: 'admin/3_auth_provider.html'}
        ],
        'admin/applications': [
            {question: 'What integration mechanisms are available?', answerUrl: 'home_provider/1_summary.html'},
            {question: 'How to import data using API-based sync?', answerUrl: 'home_provider/2_api_import.html'},
            {question: 'How to import data using SFTP transactions?', answerUrl: 'home_provider/3_sftp_import.html'},
            {question: 'How to import data using CSV upload?', answerUrl: 'home_provider/4_csv_upload.html'},
            {question: 'How to solve configuration issues?', answerUrl: 'home_provider/5_config_issues.html'}
        ],
        'admin/gallery': [
            {question: 'How to find and connect new Apps?', answerUrl: 'app_gallery/1_catalog.html'},
            {question: 'Choose what to share and sync', answerUrl: 'app_gallery/2_sharing.html'},
            {question: 'How to monitor data export Apps and suspend access to the data?', answerUrl: 'app_gallery/3_monitor.html'},
            {question: 'Add or exclude data from synchronization', answerUrl: 'app_gallery/4_requests.html'},
            // {question: 'How to solve configuration issues?', answerUrl: 'app_gallery/5_solve_issues.html'}
        ],
        'admin/applicationshistory': [
            {question: 'How to use Data Sync History?', answerUrl: 'sync_history/1_summary.html'},
            {question: 'How to manage the sync schedule?', answerUrl: 'sync_history/2_schedule.html'},
            // {question: 'Monitor sync and manage data', answerUrl: 'sync_history/3_monitor.html'},
            {question: 'Typical issues and troubleshooting', answerUrl: 'sync_history/4_issues.html'}
        ],
        'admin/reports': [
            {question: 'What data quality issues may show up here?', answerUrl: 'data_quality/1_issue.html'},
            {question: 'How to review data quality?', answerUrl: 'data_quality/2_review.html'},
            {question: 'How to resolve errors?', answerUrl: 'data_quality/3_resolve.html'}
        ],
        'databrowsing': [
            {question: 'How to browse the data?', answerUrl: 'browsing/4_browse_data.html'},
            {question: 'How to use filtering to narrow search results?', answerUrl: 'browsing/5_filtering.html'},
            {question: 'How to get all data entity\'s attributes and IDs?', answerUrl: 'browsing/6_all_data.html'}
        ],
        'user/profile': [
            {question: 'How to edit your profile?', answerUrl: 'user_profile/1_provider_profile.html'},
            {question: 'How to setup email notifications?', answerUrl: 'user_profile/2_provider_notification.html'},
        ]

    };


    readonly SYS_ADMIN_HELP_DOC_ROUTES  = {
        'dashboard/main': [
            {question: 'How to use this screen?', answerUrl: 'dashboard/summary.html'}
        ],
        'administrations': [
            {question: 'How to create, edit or remove accounts?', answerUrl: 'admin/1_provider_account.html'},
            {question: 'How to delete account and wipe all data?', answerUrl: 'admin/2_wipe.html'},
            {question: 'How to solve authentication issues?', answerUrl: 'admin/3_auth_provider.html'}
        ]
    };

    readonly AMPLIFY_HELP_LINKS = {
        'administrations': 'https://my.amplify.com/help/en/articles/5232732-amplify-enrollment-portal-administration',
        'admin/applications': 'https://my.amplify.com/help/en/articles/5232703-amplify-enrollment-portal-home',
        'admin/gallery': 'https://my.amplify.com/help/en/articles/5232704-amplify-enrollment-portal-application-gallery',
        'admin/applicationshistory': 'https://my.amplify.com/help/en/articles/5232706-amplify-enrollment-portal-data-sync-history',
        'admin/reports': 'https://my.amplify.com/help/en/articles/5232708-amplify-enrollment-portal-data-quality',
        'databrowsing': 'https://my.amplify.com/help/en/articles/5232719-amplify-enrollment-portal-data-browsing'
    };

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private activatedRoute: ActivatedRoute,
        private sanitized: DomSanitizer,
        private http: HttpClient
    ) { }

    isHelpExist(path: string) {
        let result = false;
        if (this.authenticatedUserService.isVendor(null)) {
            result = <boolean>this.APPLICATION_ADMIN_HELP_DOC_ROUTES[path];
        } else {
            if (this.authenticatedUserService.isProvider(null)) {
                result = <boolean>this.INSTITUTIONAL_ADMIN_HELP_DOC_ROUTES[path];
            } else {
                if (this.authenticatedUserService.isSysAdmin(null)) {
                    result = <boolean>this.SYS_ADMIN_HELP_DOC_ROUTES[path];
                }
            }
        }
        return result;
    }

    isShowAmplifyHelpLink(): boolean {
        return this.authenticatedUserService.isProvider(null) &&
            this.AMPLIFY_HELP_LINKS.hasOwnProperty(this.getStringFullParameterisedPath(this.activatedRoute));
    }

    getQuestions(path: string) {
        if (this.authenticatedUserService.isVendor(null)) {
            this.helpItems = this.APPLICATION_ADMIN_HELP_DOC_ROUTES[path];
        } else {
            if (this.authenticatedUserService.isProvider(null)) {
                this.helpItems = this.INSTITUTIONAL_ADMIN_HELP_DOC_ROUTES[path];
            } else {
                if (this.authenticatedUserService.isSysAdmin(null)) {
                    this.helpItems = this.SYS_ADMIN_HELP_DOC_ROUTES[path];
                }
            }
        }
        return this.helpItems;
    }

    loadAnswers() {
        if (this.helpItems.filter(item => (item.safeHtmlDoc == undefined)).length > 0) {
            this.helpItems.forEach(item => {
                this.http.get(`${this.basePath}` + `${item.answerUrl}`, {responseType: 'text' as const})
                    .subscribe((data: string) => {
                        if (environment.customer.customerShortName !== 'SchoolDay') {
                            data = data.replace(/SchoolDay/g, environment.customer.customerShortName);
                        }
                        item.safeHtmlDoc = this.sanitized.bypassSecurityTrustHtml(data);
                    },
                    error => {
                        item.hasDataLoadErrors = true;
                        console.log(error);
                    });
            });
        }
    }

    getFullParameterisedPath(route: ActivatedRoute): string[] {
        let path: string[] = [];

        if (route.children[0] && route.children[0].children[0]) {
            path = route.children[0] ? [route.children[0].routeConfig.path].concat(this.getFullParameterisedPath(route.children[0])) : [];
        } else {
            path.push(route.children[0].routeConfig.path);
        }

        return path;
    }

    getStringFullParameterisedPath(route: ActivatedRoute): string {
        return this.getFullParameterisedPath(route).filter(path => !!path).join('/');
    }

    navigateToAmplifyHelpLink() {
        window.open(this.AMPLIFY_HELP_LINKS[this.getStringFullParameterisedPath(this.activatedRoute)], '_blank');
    }
}
